package com.patronat.top.ui.molecules.pagination

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.ui.theme.*

@Composable
fun TablePagination(
    currentPage: Int,
    totalItems: Int,
    pageSize: Int,
    onPageChange: (Int) -> Unit,
    onPageSizeChange: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    val totalPages = (totalItems + pageSize - 1) / pageSize
    val safeTotalPages = if (totalPages == 0) 1 else totalPages

    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 24.dp, vertical = 16.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        RowsPerPageSelector(
            totalItems = totalItems,
            pageSize = pageSize,
            onPageSizeChange = onPageSizeChange
        )

        PageNavigation(
            currentPage = currentPage,
            totalPages = safeTotalPages,
            onPageChange = onPageChange
        )
    }
}

@Composable
private fun RowsPerPageSelector(
    totalItems: Int,
    pageSize: Int,
    onPageSizeChange: (Int) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val pageSizes = listOf(20, 50, 100)

    Row(verticalAlignment = Alignment.CenterVertically) {
        Text(
            text = "Показувати",
            style = BodyMedium.copy(color = MineShaft500),
            fontSize = 14.sp
        )

        Spacer(modifier = Modifier.width(8.dp))

        Box {
            Box(
                modifier = Modifier
                    .border(1.dp, MineShaft200, RoundedCornerShape(4.dp))
                    .clickable { expanded = true }
                    .padding(horizontal = 12.dp, vertical = 6.dp),
                contentAlignment = Alignment.Center
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = pageSize.toString(),
                        style = LabelLarge,
                        color = MineShaft900
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Icon(
                        painter = AppIcons.ArrowDown,
                        contentDescription = null,
                        tint = MineShaft500,
                        modifier = Modifier.size(16.dp)
                    )
                }
            }
            DropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                modifier = Modifier
                    .width(224.dp)
                    .background(White)
                    .border(0.75.dp, DropdownBorder, RoundedCornerShape(12.dp))
                    .padding(5.dp)
            ) {
                pageSizes.forEach { size ->
                    DropdownMenuItem(
                        text = { Text(size.toString()) },
                        onClick = {
                            onPageSizeChange(size)
                            expanded = false
                        }
                    )
                }
            }
        }


        Spacer(modifier = Modifier.width(8.dp))

        Text(
            text = "з $totalItems",
            style = BodyMedium.copy(color = MineShaft500),
            fontSize = 14.sp
        )
    }
}

@Composable
private fun PageNavigation(
    currentPage: Int,
    totalPages: Int,
    onPageChange: (Int) -> Unit
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        ArrowButton(
            icon = AppIcons.ArrowLeft,
            enabled = currentPage > 1,
            onClick = { onPageChange(currentPage - 1) }
        )

        Spacer(modifier = Modifier.width(4.dp))

        val pages = remember(currentPage, totalPages) {
            calculatePageList(currentPage, totalPages)
        }

        pages.forEach { page ->
            if (page == null) {
                Box(
                    modifier = Modifier.size(32.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = "...",
                        style = LabelLarge,
                        color = MineShaft500
                    )
                }
            } else {
                PageNumberButton(
                    page = page,
                    isSelected = page == currentPage,
                    onClick = { onPageChange(page) }
                )
            }
        }

        Spacer(modifier = Modifier.width(4.dp))

        ArrowButton(
            icon = AppIcons.ArrowLeft,
            enabled = currentPage < totalPages,
            onClick = { onPageChange(currentPage + 1) },
            rotate = 180f
        )
    }
}

@Composable
private fun PageNumberButton(
    page: Int,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .size(32.dp)
            .clip(RoundedCornerShape(8.dp))
            .border(
                width = if (isSelected) 1.dp else 0.dp,
                color = if (isSelected) MineShaft900 else Color.Transparent,
                shape = RoundedCornerShape(8.dp)
            )
            .clickable(onClick = onClick)
            .background(Color.Transparent),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = page.toString(),
            style = LabelLarge.copy(
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal,
                color = MineShaft900
            )
        )
    }
}

@Composable
private fun ArrowButton(
    icon: androidx.compose.ui.graphics.painter.Painter,
    enabled: Boolean,
    onClick: () -> Unit,
    rotate: Float = 0f
) {
    val borderColor = if (enabled) MineShaft200 else MineShaft100
    val iconColor = if (enabled) MineShaft500 else MineShaft200

    Box(
        modifier = Modifier
            .size(32.dp)
            .clip(RoundedCornerShape(8.dp))
            .border(1.dp, borderColor, RoundedCornerShape(8.dp))
            .clickable(enabled = enabled, onClick = onClick)
            .background(Color.White),
        contentAlignment = Alignment.Center
    ) {
        Icon(
            painter = icon,
            contentDescription = null,
            tint = iconColor,
            modifier = Modifier
                .size(16.dp)
                .rotate(rotate)
        )
    }
}

private fun calculatePageList(current: Int, total: Int): List<Int?> {
    if (total <= 7) {
        return (1..total).toList()
    }

    val pages = mutableListOf<Int?>()
    pages.add(1)

    if (current > 4) {
        pages.add(null)
    }

    val start = maxOf(2, current - 2)
    val end = minOf(total - 1, current + 2)

    if (start > 2) {
        pages.add(null)
    } else if (total > 2) {
        pages.add(2)
    }

    val rangeStart = if (start <= 2) 3 else start
    val rangeEnd = if (end >= total - 1) total - 2 else end

    for (i in rangeStart..rangeEnd) {
        if (!pages.contains(i)) pages.add(i)
    }

    if (end < total - 1) {
        pages.add(null)
    } else if (total > 1 && !pages.contains(total - 1)) {
        pages.add(total - 1)
    }

    if (!pages.contains(total)) {
        pages.add(total)
    }

    return pages.distinct()
}
